import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { Cpu, MemoryStick, HardDrive, Clock, Activity, AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import StatPill from './StatPill';

function utilColor(v) {
    if (v == null) return '#64748b';
    if (v >= 85) return '#ef4444';
    if (v >= 70) return '#f59e0b';
    return '#10b981';
}
function fmtUptime(sec) {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    if (d > 0) return `${d}d ${h}h`;
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}

const SEV_LABEL = ['Not classified', 'Information', 'Warning', 'Average', 'High', 'Disaster'];
const SEV_TONE = ['muted', 'info', 'warn', 'warn', 'crit', 'crit'];

export default function HostCard({ host, onClick }) {
    const online = host.available === 1
        || host.metrics?.agentPing === 1
        || host.metrics?.icmpPing === 1
        || host.metrics?.icmpReachable === 1;
    const statusTone = online ? 'good' : 'crit';
    const coverageBad = host.metricCoverage && !host.metricCoverage.ok && host.metricCoverage.expected?.length > 0;
    const templateBad = !host.templateOk;
    const topProblem = (host.problems || []).slice().sort((a, b) => (b.severity || 0) - (a.severity || 0))[0];
    const clickable = typeof onClick === 'function';

    return (
        <GlassPaper
            onClick={clickable ? onClick : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
            tabIndex={clickable ? 0 : undefined}
            role={clickable ? 'button' : undefined}
            sx={{
                p: { xs: 1.75, sm: 2 },
                height: '100%',
                display: 'flex', flexDirection: 'column', gap: 1.25,
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    ...(clickable && { boxShadow: '0 10px 24px rgba(59,130,246,0.18)', borderColor: 'rgba(96,165,250,0.45)' }),
                },
                '&:focus-visible': clickable ? { outline: '2px solid rgba(96,165,250,0.55)', outlineOffset: 2 } : undefined,
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                        fontSize: { xs: 14, sm: 14.5 }, fontWeight: 700, lineHeight: 1.2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{host.name}</Typography>
                    <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 0.25 }}>
                        {host.ip || host.host}
                        {host.kind && host.kind !== 'unknown' && <> · <span style={{ textTransform: 'capitalize' }}>{host.kind}</span></>}
                    </Typography>
                </Box>
                <StatPill tone={statusTone} icon={<Activity size={11} />}>
                    {online ? 'Up' : 'Down'}
                </StatPill>
            </Box>

            {/* Template + coverage warnings */}
            {(templateBad || coverageBad) && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {templateBad && (
                        <Tooltip title={host.templates?.length ? `Templates: ${host.templates.join(', ')}` : 'No templates assigned to this host in Zabbix'}>
                            <span><StatPill tone="warn" icon={<ShieldAlert size={11} />}>Template</StatPill></span>
                        </Tooltip>
                    )}
                    {coverageBad && (
                        <Tooltip title={`Missing metrics: ${host.metricCoverage.missing.join(', ')}`}>
                            <span><StatPill tone="warn" icon={<AlertTriangle size={11} />}>Metrics</StatPill></span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {topProblem && (
                <Tooltip title={topProblem.name}>
                    <Box>
                        <StatPill tone={SEV_TONE[topProblem.severity] || 'warn'} icon={<AlertTriangle size={11} />}>
                            {SEV_LABEL[topProblem.severity] || 'Problem'}
                        </StatPill>
                    </Box>
                </Tooltip>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 0.5 }}>
                <Metric icon={<Cpu size={13} />} label="CPU" value={host.metrics?.cpuPct} />
                <Metric icon={<MemoryStick size={13} />} label="Memory" value={host.metrics?.memPct} />
                <Metric icon={<HardDrive size={13} />} label="Disk" value={host.metrics?.rootDiskPct} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto', pt: 0.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11.5, opacity: 0.72 }}>
                    <Clock size={12} /> {fmtUptime(host.metrics?.uptimeSec)}
                </Box>
                {host.metrics?.loadAvg1 != null && (
                    <Box sx={{ fontSize: 11.5, opacity: 0.72 }}>
                        load {host.metrics.loadAvg1.toFixed(2)}
                    </Box>
                )}
                {!topProblem && !templateBad && !coverageBad && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11.5, opacity: 0.72, ml: 'auto', color: '#34d399' }}>
                        <CheckCircle2 size={12} /> Healthy
                    </Box>
                )}
            </Box>
        </GlassPaper>
    );
}

function Metric({ icon, label, value }) {
    const v = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
    const color = utilColor(v);
    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
                    {icon}{label}
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color }}>
                    {v != null ? `${v.toFixed(0)}%` : '—'}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={v ?? 0}
                sx={{
                    height: 5,
                    borderRadius: 999,
                    bgcolor: 'rgba(148,163,184,0.16)',
                    '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: color },
                }}
            />
        </Box>
    );
}
